import { PRODUCTS_FORM_INFOS } from '@/consts/products';
import useDataLayer from '@/hooks/useDataLayer/useDataLayer';
import useNavigate from '@/hooks/useNavigate/useNavigate';
import Button from '@uikit/components/Button/Button';
import {
  ButtonAlign,
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonWidth,
  ShowAddon,
  ShowIcon,
} from '@uikit/components/Button/consts';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import FormFieldDropdown from '@uikit/components/FormFieldDropdown/FormFieldDropdown';
import { FormFieldDropdownOption } from '@uikit/components/FormFieldDropdown/interfaces';
import { useState, type FormEventHandler } from 'react';
import { PRODUCT_BUTTON_ICONS, PRODUCT_OPTIONS_ORDER } from './consts';
import type {
  ProductFormType,
  ProductOption,
  StageBoxTariffFormProps,
} from './interfaces';

const sortProductOptions = (options: ProductOption[]) =>
  options.sort(
    (a, b) =>
      PRODUCT_OPTIONS_ORDER.indexOf(a) - PRODUCT_OPTIONS_ORDER.indexOf(b)
  );

const productOptionFromProductFormType = (productFormType: ProductFormType) =>
  (productFormType.split('-') as ProductOption[]) || [];

const StageBoxTariffForm = ({
  productFormType,
  configuratorBaseUrl,
  powerGasCombinedDefault,
  className,
  classNameFormGroup,
  isBoxOnGradient = true,
  submitButtonColor = ButtonColor.Secondary,
  submitButtonAlign = ButtonAlign.Left,
  submitButtonAddon = ShowAddon.Gradient,
  submitButtonWidth = ButtonWidth.Auto,
}: StageBoxTariffFormProps) => {
  const { pushDataLayerEvent } = useDataLayer();
  const navigate = useNavigate();

  const productOptions: ProductOption[] = sortProductOptions(
    productOptionFromProductFormType(productFormType)
  );

  const [productOption, setProductOption] = useState<ProductOption>(
    powerGasCombinedDefault ?? productOptions.find(Boolean) ?? 'power'
  );

  const handleOnSubmit: FormEventHandler<HTMLFormElement> = ($event) => {
    $event?.preventDefault();

    const currentOption = PRODUCTS_FORM_INFOS[productOption];

    pushDataLayerEvent({
      event: 'customClick2',
      isLeadingTo: 'S&G-Konfigurator',
      isSgkModulePresent: 'true',
      sgkProductSelection: currentOption.radioLabel,
    });

    const configuratorUrl = new URL(
      currentOption.configuratorPath,
      configuratorBaseUrl
    ).toString();

    navigate(configuratorUrl);
  };

  const productDropdownOptions: FormFieldDropdownOption[] = productOptions.map(
    (value) => {
      return {
        label: PRODUCTS_FORM_INFOS[value].radioLabel,
        value,
      };
    }
  );

  const showDropdown = productDropdownOptions.length > 1;

  const handleOnDropdownChange = (option: FormFieldDropdownOption | null) => {
    if (option?.value) {
      setProductOption(option.value as ProductOption);
    }
  };

  return (
    <form onSubmit={handleOnSubmit} className={className}>
      <div className={classNameFormGroup}>
        {showDropdown && (
          <FormFieldDropdown
            options={productDropdownOptions}
            label="Tarife anzeigen für"
            name="productOption"
            value={productOption}
            isBoxOnGradient={isBoxOnGradient}
            onChange={handleOnDropdownChange}
          />
        )}
      </div>
      <Button
        type={ButtonType.Submit}
        actionType={ClickableAction.None}
        align={submitButtonAlign}
        color={submitButtonColor}
        width={submitButtonWidth}
        showAddon={submitButtonAddon}
        showIcon={ShowIcon.ShowIcon}
        icon={PRODUCT_BUTTON_ICONS[productOption]}
        size={ButtonSize.Large}
        noClickable
      >
        {PRODUCTS_FORM_INFOS[productOption].buttonText}
      </Button>
    </form>
  );
};
export default StageBoxTariffForm;
